import { ApiClientResponseError } from "@verity/api-client";
import { AssessmentUuids, LiveSession, LiveSessionUpdate, VerityApi } from "../../api/fetch";
import { changeTimeOnly } from "../../core/datetime-picker/TimePicker";
import i18nPackage from "../../i18n/i18n";

export const ONE_MIN_MS = 60 * 1000;
export const ONE_HOUR_MS = 60 * ONE_MIN_MS;
export const ONE_DAY_MS = 24 * ONE_HOUR_MS;

export const MIN_DURATION = ONE_MIN_MS;
export const MAX_DURATION = 7 * ONE_DAY_MS - 1;
export const MAX_DURATION_DEMO = 365 * ONE_DAY_MS - 1;

export type ProctorInfo = { id: string; name?: string };

export type ProctorGroupInfo = {
	id: number;
	name: string;
	minHoursNotice: number;
	trackCancellationHoursBefore: number;
	trackCancellationMessage?: string;
	autoCancelEmptyHoursBefore?: number;
};

export type LiveSessionWithInvitations = LiveSession & {
	invitations: { liveSessionId: string; proctorGroupId: number }[];
};

export type PendingLiveSession = Pick<
	LiveSessionUpdate,
	"name" | "startTime" | "endTime" | "capacity" | "proctorGuide"
> & {
	identifier: string;
	id?: string;
	proctorId?: string;
	proctorGroupId?: number;
	registrationStartTime?: Date;
	registrationEndTime?: Date;
};

export type EditQuizAssessmentUUids = Omit<AssessmentUuids, "assessmentUuid"> & {
	assessmentUuid?: string;
};

export type LmsScheduleInfo = {
	timeLimitMinutes?: number;
	availableFrom?: string;
	availableUntil?: string;
};

type AssessmentWithLmsSchedule = {
	quizInfo?: Record<string, unknown> | null;
};

export type SessionIssue =
	| "noName"
	| "noSessions"
	| "timesInvalid"
	| "startTimePassed"
	| "endTimePassed"
	| "durationTooLong"
	| "capacityInvalid"
	| "noProctor"
	| "proctorGroupNotice"
	| "registrationTimesInvalid"
	| "registrationStartTimePassed"
	| "registrationEndTimePassed"
	| "registrationStartTimeAfterStartTime"
	| "registrationEndTimeAfterEndTime"
	| "registrationEndTimeAfterCancellationTime"
	| "unfilledRegistrationTimeField";

function convertLiveSessionToPendingSession({
	id,
	name,
	startTime,
	endTime,
	capacity,
	registrationStartTime,
	registrationEndTime,
	users,
	invitations,
	proctorGuide,
}: LiveSessionWithInvitations): PendingLiveSession {
	return {
		identifier: id,
		id,
		name,
		startTime: new Date(startTime),
		endTime: new Date(endTime),
		proctorGuide: proctorGuide ?? undefined,
		capacity,
		proctorId: users.find(({ role }) => role === "proctor")?.userId,
		proctorGroupId: invitations[0]?.proctorGroupId,
		registrationStartTime: registrationStartTime == null ? undefined : new Date(registrationStartTime),
		registrationEndTime: registrationEndTime == null ? undefined : new Date(registrationEndTime),
	};
}

export function convertLiveSessionsToPendingSessions(
	sessions: LiveSessionWithInvitations[],
): Record<string, PendingLiveSession> {
	return Object.fromEntries(sessions.map((session) => [session.id, convertLiveSessionToPendingSession(session)]));
}

export function isLiveSessionNameChanged(existingSession: LiveSession, { name }: PendingLiveSession) {
	return existingSession.name !== name;
}

export function areLiveSessionTimesChanged(existingSession: LiveSession, { startTime, endTime }: PendingLiveSession) {
	return (
		new Date(existingSession.startTime).getTime() !== startTime.getTime() ||
		new Date(existingSession.endTime).getTime() !== endTime.getTime()
	);
}

export function isLiveSessionCapacityChanged(existingSession: LiveSession, { capacity }: PendingLiveSession) {
	return existingSession.capacity !== capacity;
}

export function isLiveSessionProctorGuideChanged(existingSession: LiveSession, { proctorGuide }: PendingLiveSession) {
	return existingSession.proctorGuide != proctorGuide;
}

function isNullableTimeStringEqual(existingTime?: string | null, newTime?: Date) {
	const existingTimestamp = existingTime ? new Date(existingTime).getTime() : undefined;
	return existingTimestamp === newTime?.getTime();
}

export function areLiveSessionRegistrationTimesChanged(
	existingSession: LiveSession,
	{ registrationStartTime, registrationEndTime }: PendingLiveSession,
) {
	return (
		!isNullableTimeStringEqual(existingSession.registrationStartTime, registrationStartTime) ||
		!isNullableTimeStringEqual(existingSession.registrationEndTime, registrationEndTime)
	);
}

export function isLiveSessionEntityChanged(existingSession: LiveSession, pendingSession: PendingLiveSession) {
	return (
		isLiveSessionNameChanged(existingSession, pendingSession) ||
		areLiveSessionTimesChanged(existingSession, pendingSession) ||
		isLiveSessionCapacityChanged(existingSession, pendingSession) ||
		areLiveSessionRegistrationTimesChanged(existingSession, pendingSession) ||
		isLiveSessionProctorGuideChanged(existingSession, pendingSession)
	);
}

export function isLiveSessionProctorChanged(
	existingSession: LiveSessionWithInvitations,
	{ proctorId }: PendingLiveSession,
) {
	return (
		(proctorId && !existingSession.users.some(({ userId, role }) => role === "proctor" && userId === proctorId)) ||
		(!proctorId && existingSession.users.length > 0 && existingSession.invitations.length === 0)
	);
}

export function isLiveSessionProctorGroupChanged(
	existingSession: LiveSessionWithInvitations,
	{ proctorGroupId }: PendingLiveSession,
) {
	return (
		(proctorGroupId &&
			!existingSession.invitations.some((proctorGroup) => proctorGroup.proctorGroupId === proctorGroupId)) ||
		(!proctorGroupId && existingSession.invitations.length > 0)
	);
}

export function isLiveSessionDemoMode({ name }: PendingLiveSession) {
	return name.trim().endsWith("(Demo)");
}

export function checkProctorGroupNotice(
	startTime: PendingLiveSession["startTime"],
	{ minHoursNotice }: ProctorGroupInfo,
) {
	const currTimestamp = Date.now();
	return !minHoursNotice || currTimestamp < startTime.getTime() - minHoursNotice * 60 * 60 * 1000;
}

export function getProctorGroupCancellationDeadline(startTime: Date, autoCancelEmptyHoursBefore?: number): Date | null {
	if (autoCancelEmptyHoursBefore == null) return null;
	return new Date(startTime.getTime() - autoCancelEmptyHoursBefore * ONE_HOUR_MS);
}

export function checkProctorGroupCancellationTime(
	{ startTime, registrationEndTime }: PendingLiveSession,
	{ autoCancelEmptyHoursBefore }: ProctorGroupInfo,
) {
	const deadline = getProctorGroupCancellationDeadline(startTime, autoCancelEmptyHoursBefore);
	if (!deadline || !registrationEndTime) return true;

	return registrationEndTime.getTime() <= deadline.getTime();
}

export function getPotentiallyCancelledInvitations(
	existingSession: LiveSessionWithInvitations,
	proctorGroups: Record<number, ProctorGroupInfo>,
) {
	const currTimestamp = Date.now();
	return existingSession.invitations.filter(({ proctorGroupId }) => {
		const proctorGroup = proctorGroups[proctorGroupId];
		return (
			proctorGroup?.trackCancellationHoursBefore &&
			proctorGroup?.trackCancellationMessage &&
			currTimestamp >=
				new Date(existingSession.startTime).getTime() - proctorGroup.trackCancellationHoursBefore * 60 * 60 * 1000
		);
	});
}

export function getLiveSessionsCancelledByUpdate(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
	proctorGroups: Record<number, ProctorGroupInfo>,
) {
	return Object.values(pendingSessions).filter((pendingSession) => {
		const existingSession = pendingSession.id ? existingSessions[pendingSession.id] : undefined;
		return (
			existingSession &&
			isLiveSessionProctorGroupChanged(existingSession, pendingSession) &&
			getPotentiallyCancelledInvitations(existingSession, proctorGroups).length > 0
		);
	});
}

export function getRegistrationErrors(
	session: PendingLiveSession,
	currTime: Date,
	proctorGroups?: Record<number, ProctorGroupInfo>,
	existingSession?: LiveSessionWithInvitations,
): SessionIssue[] {
	const { startTime, endTime, proctorGroupId, registrationStartTime, registrationEndTime } = session;

	const timesChanged = !existingSession || areLiveSessionTimesChanged(existingSession, session);
	const registrationTimesChanged = !existingSession || areLiveSessionRegistrationTimesChanged(existingSession, session);
	const proctorGroupChanged = !existingSession || isLiveSessionProctorGroupChanged(existingSession, session);

	const issues: SessionIssue[] = [];

	if (registrationTimesChanged) {
		if (registrationStartTime && registrationEndTime && registrationStartTime >= registrationEndTime) {
			issues.push("registrationTimesInvalid");
		}
		if (registrationEndTime && registrationEndTime <= currTime) {
			issues.push("registrationEndTimePassed");
		}
		if (registrationStartTime && registrationStartTime <= currTime) {
			issues.push("registrationStartTimePassed");
		}
		if (!registrationStartTime !== !registrationEndTime) {
			issues.push("unfilledRegistrationTimeField");
		}
	}

	if (timesChanged || registrationTimesChanged) {
		if (registrationStartTime && currTime < startTime && registrationStartTime > startTime) {
			issues.push("registrationStartTimeAfterStartTime");
		}
		if (registrationEndTime && registrationEndTime > endTime) {
			issues.push("registrationEndTimeAfterEndTime");
		}
	}

	if ((timesChanged || registrationTimesChanged || proctorGroupChanged) && proctorGroupId && proctorGroups) {
		const proctorGroup = proctorGroups[proctorGroupId];
		if (proctorGroup && !checkProctorGroupCancellationTime(session, proctorGroup)) {
			issues.push("registrationEndTimeAfterCancellationTime");
		}
	}

	return issues;
}

export function getLiveSessionErrors(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
	proctorGroups: Record<number, ProctorGroupInfo>,
): Record<string, SessionIssue[]> {
	const currTime = new Date();
	if (Object.keys(pendingSessions).length === 0) {
		return { _: ["noSessions"] };
	}
	return Object.fromEntries(
		Object.values(pendingSessions).map((pendingSession) => {
			const { identifier, name, startTime, endTime, capacity, proctorId, proctorGroupId } = pendingSession;
			const existingSession = existingSessions[identifier];
			const nameChanged = !existingSession || isLiveSessionNameChanged(existingSession, pendingSession);
			const timesChanged = !existingSession || areLiveSessionTimesChanged(existingSession, pendingSession);
			const capacityChanged = !existingSession || isLiveSessionCapacityChanged(existingSession, pendingSession);

			const proctorGroupChanged = !existingSession || isLiveSessionProctorGroupChanged(existingSession, pendingSession);
			const isCancelled =
				existingSession &&
				getPotentiallyCancelledInvitations(existingSession, proctorGroups).length > 0 &&
				proctorGroupChanged;
			const proctorChanged =
				!existingSession || isLiveSessionProctorChanged(existingSession, pendingSession) || proctorGroupChanged;

			const issues: SessionIssue[] = [];
			if (nameChanged && !name) {
				issues.push("noName");
			}
			if (timesChanged) {
				if (startTime >= endTime) {
					issues.push("timesInvalid");
				}
				if (startTime <= currTime) {
					issues.push("startTimePassed");
				}
				if (endTime <= currTime) {
					issues.push("endTimePassed");
				}
				if (!isLiveSessionDemoMode(pendingSession) && endTime.getTime() - startTime.getTime() > MAX_DURATION) {
					issues.push("durationTooLong");
				}
			}
			const registrationIssues = getRegistrationErrors(pendingSession, currTime, proctorGroups, existingSession);
			issues.push(...registrationIssues);

			if (capacityChanged && capacity <= 0) {
				issues.push("capacityInvalid");
			}
			if (proctorChanged && !proctorId && !proctorGroupId) {
				issues.push("noProctor");
			} else if ((proctorChanged || isCancelled) && proctorGroupId) {
				const proctorGroup = proctorGroups[proctorGroupId];
				if (proctorGroup && !checkProctorGroupNotice(pendingSession.startTime, proctorGroup)) {
					issues.push("proctorGroupNotice");
				}
			}
			return [identifier, issues] satisfies [string, SessionIssue[]];
		}),
	);
}

export function spansMultipleDays(startTime: Date, endTime: Date) {
	return changeTimeOnly(startTime, 0, 0).getTime() !== changeTimeOnly(endTime, 0, 0).getTime();
}

export function resetStartedLiveSessions(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
): Record<string, PendingLiveSession> {
	const currTime = new Date();
	return Object.fromEntries(
		Object.entries(pendingSessions)
			.map(([identifier, session]): [string, PendingLiveSession] => {
				const existingSession = session.id ? existingSessions[session.id] : undefined;
				if (existingSession && currTime >= new Date(existingSession.startTime)) {
					const { name, startTime, endTime, capacity, proctorGroupId } =
						convertLiveSessionToPendingSession(existingSession);
					return [
						identifier,
						{
							...session,
							name,
							startTime,
							endTime,
							capacity,
							proctorId: proctorGroupId ? undefined : session.proctorId,
							proctorGroupId,
						},
					];
				}
				return [identifier, session];
			})
			// restore deleted sessions
			.concat(
				Object.values(existingSessions)
					.filter(({ id, startTime }) => !(id in pendingSessions) && currTime >= new Date(startTime))
					.map((session) => [session.id, convertLiveSessionToPendingSession(session)]),
			),
	);
}

function getCreatedLiveSessions(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
) {
	return Object.values(pendingSessions).filter(({ identifier }) => !(identifier in existingSessions));
}

function getDeletedLiveSessions(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
) {
	return Object.values(existingSessions).filter(({ id }) => !(id in pendingSessions));
}

function getLiveSessionsWithExisting(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
) {
	return Object.values(pendingSessions)
		.map(
			(session) =>
				[session, session.id ? existingSessions[session.id] : undefined] satisfies [
					PendingLiveSession,
					LiveSessionWithInvitations | undefined,
				],
		)
		.filter(
			(
				sessions,
			): sessions is [
				PendingLiveSession & {
					id: string;
				},
				LiveSessionWithInvitations,
			] => !!sessions[1],
		);
}

function getUpdatedLiveSessions(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
) {
	return getLiveSessionsWithExisting(existingSessions, pendingSessions).filter(([pendingSession, existingSession]) =>
		isLiveSessionEntityChanged(existingSession, pendingSession),
	);
}

type ProctorIds = { liveSessionId: string; userId: string };

function getProctorModifications(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
): [ProctorIds[], ProctorIds[]] {
	const addedProctors: ProctorIds[] = [];
	let removedProctors: ProctorIds[] = [];
	const updatedSessions = getLiveSessionsWithExisting(existingSessions, pendingSessions);
	updatedSessions.forEach(([pendingSession, existingSession]) => {
		if (isLiveSessionProctorChanged(existingSession, pendingSession)) {
			removedProctors = removedProctors.concat(existingSession.users.filter(({ role }) => role === "proctor"));
			if (pendingSession.proctorId) {
				addedProctors.push({ liveSessionId: pendingSession.id, userId: pendingSession.proctorId });
			}
		}
	});
	return [addedProctors, removedProctors];
}

type InvitationIds = { liveSessionId: string; proctorGroupId: number };

function getAddedInvitations(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
) {
	const addedInvitations: InvitationIds[] = [];
	getLiveSessionsWithExisting(existingSessions, pendingSessions).forEach(([pendingSession, existingSession]) => {
		if (isLiveSessionProctorGroupChanged(existingSession, pendingSession)) {
			if (pendingSession.proctorGroupId) {
				addedInvitations.push({
					liveSessionId: pendingSession.id,
					proctorGroupId: pendingSession.proctorGroupId,
				});
			}
		}
	});
	getCreatedLiveSessions(existingSessions, pendingSessions).forEach(({ id, proctorGroupId }) => {
		if (id && proctorGroupId) {
			addedInvitations.push({ liveSessionId: id, proctorGroupId });
		}
	});
	return addedInvitations;
}

function getRemovedInvitations(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
) {
	let removedInvitations: InvitationIds[] = [];
	getLiveSessionsWithExisting(existingSessions, pendingSessions).forEach(([pendingSession, existingSession]) => {
		if (isLiveSessionProctorGroupChanged(existingSession, pendingSession)) {
			removedInvitations = removedInvitations.concat(existingSession.invitations);
		}
	});
	return removedInvitations;
}

type SaveLiveSessionErrorDetails = { sessionId: string; error: unknown };

function getErrorMessage(
	error: unknown,
	existingSession?: LiveSessionWithInvitations,
	pendingSession?: PendingLiveSession,
) {
	const sessionName = (pendingSession ?? existingSession)?.name;
	let message = i18nPackage.t("editQuizPage.liveSessionStep.error.default", { sessionName });
	if (error instanceof ApiClientResponseError) {
		if (error.status === 413) {
			message = i18nPackage.t("editQuizPage.liveSessionStep.error.proctorGuideTooBig", { sessionName });
		} else {
			switch (error.code) {
				case "insufficientNotice": {
					const minHoursNotice = (
						error.details as
							| {
									proctorGroupName?: string;
									minHoursNotice?: number;
							  }
							| undefined
					)?.minHoursNotice;
					if (minHoursNotice) {
						message = i18nPackage.t("editQuizPage.liveSessionStep.error.minHoursNotice", {
							sessionName,
							count: minHoursNotice,
						});
					} else {
						message = i18nPackage.t("editQuizPage.liveSessionStep.error.insufficientNotice", { sessionName });
					}
					break;
				}
				case "timesPast":
					message = i18nPackage.t("editQuizPage.liveSessionStep.error.timesPast", { sessionName });
					break;
				case "alreadyStarted":
				case "alreadyEnded":
					message = i18nPackage.t("editQuizPage.liveSessionStep.error.alreadyEnded", { sessionName });
					break;
				case "durationTooLong":
					message = i18nPackage.t("editQuizPage.liveSessionStep.error.durationTooLong", { sessionName });
					break;
				case "registrationTimesPast":
					message = i18nPackage.t("editQuizPage.liveSessionStep.error.registrationTimesPast", { sessionName });
					break;
				default:
					if (error.originalMessage) {
						message = `${sessionName}: ${error.originalMessage}`;
					}
					break;
			}
		}
	}
	return message;
}

function getErrorMessages(
	existingSessions: Record<string, LiveSessionWithInvitations>,
	pendingSessions: Record<string, PendingLiveSession>,
	errors: SaveLiveSessionErrorDetails[],
) {
	return errors.map(({ sessionId, error }) => {
		return getErrorMessage(error, existingSessions[sessionId], pendingSessions[sessionId]);
	});
}

export class LiveSessionError extends Error {
	readonly errors: SaveLiveSessionErrorDetails[];
	readonly messages: string[];

	constructor(
		existingSessions: Record<string, LiveSessionWithInvitations>,
		pendingSessions: Record<string, PendingLiveSession>,
		errors: SaveLiveSessionErrorDetails[],
	) {
		const messages = getErrorMessages(existingSessions, pendingSessions, errors);
		super(messages.join(", "));
		this.errors = errors;
		this.messages = messages;
	}
}

export async function saveLiveSessions({
	api,
	assessmentUuids,
	existingSessions,
	pendingSessions,
}: {
	api: VerityApi;
	assessmentUuids: AssessmentUuids;
	existingSessions: Record<string, LiveSessionWithInvitations>;
	pendingSessions: Record<string, PendingLiveSession>;
}) {
	const errors: SaveLiveSessionErrorDetails[] = [];

	const newLiveSessions: Record<string, PendingLiveSession & { id: string }> = {};
	const removedInvitations = getRemovedInvitations(existingSessions, pendingSessions);
	await Promise.all(
		removedInvitations.map(({ liveSessionId, proctorGroupId }) =>
			api.deleteProctorGroupInvitation(liveSessionId, proctorGroupId).catch((error) =>
				errors.push({
					sessionId: liveSessionId,
					error,
				}),
			),
		),
	);

	await Promise.all(
		getCreatedLiveSessions(existingSessions, pendingSessions).map((session) =>
			api
				.createLiveSession(assessmentUuids, {
					...session,
					registrationStartTime: session.registrationStartTime ?? null,
					registrationEndTime: session.registrationEndTime ?? null,
					users: session.proctorId ? [{ userId: session.proctorId, role: "proctor" }] : undefined,
				})
				.then(({ id }) => {
					newLiveSessions[session.identifier] = { ...session, id };
				})
				.catch((error) => {
					errors.push({ sessionId: session.identifier, error });
				}),
		),
	);
	await Promise.all(
		getDeletedLiveSessions(existingSessions, pendingSessions).map(({ id }) =>
			api.deleteLiveSession(id).catch((error) => errors.push({ sessionId: id, error })),
		),
	);
	await Promise.all(
		getUpdatedLiveSessions(existingSessions, pendingSessions).map(([session]) =>
			api
				.updateLiveSession(session.id, {
					...session,
					registrationStartTime: session.registrationStartTime ?? null,
					registrationEndTime: session.registrationEndTime ?? null,
				})
				.catch((error) => {
					errors.push({
						sessionId: session.id,
						error,
					});
				}),
		),
	);

	if (errors.length > 0) {
		throw new LiveSessionError(existingSessions, pendingSessions, errors);
	}

	const [addedProctors, removedProctors] = getProctorModifications(existingSessions, pendingSessions);
	await Promise.all(
		addedProctors.map(({ liveSessionId, userId }) =>
			api.addLiveSessionProctor(liveSessionId, userId).catch((error) =>
				errors.push({
					sessionId: liveSessionId,
					error,
				}),
			),
		),
	);
	await Promise.all(
		removedProctors.map(({ liveSessionId, userId }) =>
			api.removeLiveSessionProctor(liveSessionId, userId).catch((error) =>
				errors.push({
					sessionId: liveSessionId,
					error,
				}),
			),
		),
	);

	const addedInvitations = getAddedInvitations(existingSessions, {
		...pendingSessions,
		...newLiveSessions,
	});
	await Promise.all(
		addedInvitations.map(({ liveSessionId, proctorGroupId }) =>
			api.createProctorGroupInvitation(liveSessionId, proctorGroupId).catch((error) =>
				errors.push({
					sessionId: liveSessionId,
					error,
				}),
			),
		),
	);

	if (errors.length > 0) {
		throw new LiveSessionError(existingSessions, pendingSessions, errors);
	}
}

export function stripSeconds(timestamp: number) {
	const newDate = new Date(timestamp);
	newDate.setSeconds(0, 0);
	return newDate;
}

type FormatDurationOptions = {
	/** @default " " (space) - The string used to join units (e.g., ", " for "1 day, 2 hours") */
	separator?: string;
	/** @default "short" - "full" (1 hour) or "short" (1h) */
	style?: "full" | "short";
};

// so typescript and i18n works
type DurationUnit = "day" | "hour" | "minute";

export function formatDuration(durationMs: number, options: FormatDurationOptions = {}) {
	const { separator = " ", style = "short" } = options;
	const totalMinutes = Math.max(0, Math.floor(durationMs / (60 * 1000)));
	const totalHours = Math.floor(totalMinutes / 60);

	const days = Math.floor(totalHours / 24);
	const hours = totalHours % 24;
	const minutes = totalMinutes % 60;

	const unitsAndValues: [DurationUnit, number][] = [
		["day", days],
		["hour", hours],
		["minute", minutes],
	];
	const result = unitsAndValues
		.filter(([, value]) => value > 0)
		.map(([unit, count]) => {
			const styleKey = style === "short" ? "_short" : "";
			const key = `editQuizPage.liveSessionStep.durationFormat.${unit}${styleKey}` as const;

			return i18nPackage.t(key, { count });
		})
		.join(separator);

	if (!result) {
		const styleKey = style === "short" ? "_short" : "";
		const fallbackKey = `editQuizPage.liveSessionStep.durationFormat.minute${styleKey}` as const;
		return i18nPackage.t(fallbackKey, { count: 0 });
	}

	return result;
}

export function formatShortDate(date: Date) {
	return date.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

export function formatTime(time: Date) {
	return time.toLocaleTimeString([], { timeStyle: "short" });
}

export function clampDate(date: Date, min: Date, max: Date) {
	if (date < min) return min;
	if (date > max) return max;
	return date;
}

function getValidDate(value: unknown): Date | undefined {
	if (typeof value !== "string" || !value.trim()) return undefined;

	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? undefined : date;
}

export function getLmsScheduleInfo(assessmentInfo: AssessmentWithLmsSchedule): LmsScheduleInfo | undefined {
	const schedule = assessmentInfo.quizInfo?.lmsSchedule;
	if (!schedule || typeof schedule !== "object" || Array.isArray(schedule)) return undefined;

	const { timeLimitMinutes, availableFrom, availableUntil } = schedule as Record<string, unknown>;
	const parsedTimeLimit = typeof timeLimitMinutes === "number" && timeLimitMinutes > 0 ? timeLimitMinutes : undefined;
	const parsedAvailableFrom = getValidDate(availableFrom)?.toISOString();
	const parsedAvailableUntil = getValidDate(availableUntil)?.toISOString();

	if (!parsedTimeLimit && !parsedAvailableFrom && !parsedAvailableUntil) return undefined;

	return {
		timeLimitMinutes: parsedTimeLimit,
		availableFrom: parsedAvailableFrom,
		availableUntil: parsedAvailableUntil,
	};
}

export function getDefaultLiveSessionDuration(assessmentInfo: AssessmentWithLmsSchedule) {
	const schedule = getLmsScheduleInfo(assessmentInfo);
	if (!schedule?.timeLimitMinutes) return ONE_HOUR_MS;

	return schedule.timeLimitMinutes * ONE_MIN_MS;
}

export function getDefaultLiveSessionTimes(assessmentInfo: AssessmentWithLmsSchedule, currTimestamp = Date.now()) {
	const schedule = getLmsScheduleInfo(assessmentInfo);
	const duration = getDefaultLiveSessionDuration(assessmentInfo);
	const startTime = stripSeconds(getValidDate(schedule?.availableFrom)?.getTime() ?? currTimestamp + ONE_HOUR_MS);

	return {
		startTime,
		endTime: stripSeconds(startTime.getTime() + duration),
	};
}

export function getLmsAvailabilityWarning(session: PendingLiveSession, schedule?: LmsScheduleInfo) {
	if (!schedule?.availableFrom && !schedule?.availableUntil) return undefined;

	const availableFrom = getValidDate(schedule.availableFrom);
	const availableUntil = getValidDate(schedule.availableUntil);
	const sessionStart = stripSeconds(session.startTime.getTime());
	const sessionEnd = stripSeconds(session.endTime.getTime());

	if (availableFrom && sessionStart < stripSeconds(availableFrom.getTime())) {
		return "startsBeforeAvailability";
	}

	if (availableUntil && sessionEnd > stripSeconds(availableUntil.getTime())) {
		return "endsAfterAvailability";
	}

	return undefined;
}

// Auto correct registration dates so both are valid
export function getValidRegistrationDates(
	session: PendingLiveSession,
	currTime: Date,
	availableProctorGroups: Record<number, ProctorGroupInfo>,
) {
	const { registrationEndTime, registrationStartTime, proctorGroupId } = session;

	if (!registrationEndTime || !registrationStartTime) return null;

	const regStart = stripSeconds(registrationStartTime.getTime());
	const regEnd = stripSeconds(registrationEndTime.getTime());

	const issues = getRegistrationErrors(session, currTime, availableProctorGroups);

	const shouldAutoCorrect = issues.some(
		(i) =>
			i === "registrationEndTimeAfterEndTime" ||
			i === "registrationStartTimeAfterStartTime" ||
			i === "registrationTimesInvalid" ||
			i === "registrationEndTimePassed" ||
			i === "registrationStartTimePassed" ||
			i === "registrationEndTimeAfterCancellationTime",
	);

	if (shouldAutoCorrect) {
		// Clamp to 1 min if the gap is negative (happens when there's a date order error)
		const idealGap = Math.max(regEnd.getTime() - regStart.getTime(), ONE_MIN_MS);
		const proctorGroup = proctorGroupId ? availableProctorGroups[proctorGroupId] : undefined;

		// Boundaries according to registration errors
		const maxRegTimes = getMaxRegistrationDates(session, proctorGroup);

		// reg start must be before or at session start
		// Try to make it 10 min before start time so users can register
		const bufferMax = new Date(maxRegTimes.maxStart.getTime() - 10 * ONE_MIN_MS);

		// Make sure start time is in the future
		const maxRegStart = new Date(Math.max(currTime.getTime(), bufferMax.getTime()));

		// Registration needs to be after current time, but add a minute bc seconds sometimes makes reg time invalid.
		const minRegStart = new Date(currTime.getTime() + ONE_MIN_MS);

		// currTime <= finalStart <= maxRegStart
		const finalStart = clampDate(regStart, minRegStart, maxRegStart);

		// Try to keep the same gap as before
		const targetEnd = new Date(finalStart.getTime() + idealGap);

		// Registration end date must be after the registration start date and must be greater than the current time
		const referenceTime = Math.max(finalStart.getTime(), currTime.getTime());
		const minRegEnd = new Date(referenceTime + ONE_MIN_MS);

		const finalEnd = clampDate(targetEnd, minRegEnd, maxRegTimes.maxEnd);

		return {
			startTime: finalStart,
			endTime: finalEnd,
		};
	}

	return null;
}

export function getMaxRegistrationDates(session: PendingLiveSession, proctorGroup?: ProctorGroupInfo) {
	const { startTime, endTime } = session;

	const maxStart = stripSeconds(startTime.getTime());
	let maxEnd = stripSeconds(endTime.getTime());

	if (proctorGroup) {
		const cancellationDeadline = getProctorGroupCancellationDeadline(
			startTime,
			proctorGroup.autoCancelEmptyHoursBefore,
		);

		// registration end time must be before the deadline
		maxEnd = cancellationDeadline && maxEnd > cancellationDeadline ? cancellationDeadline : maxEnd;
	}
	return { maxStart, maxEnd };
}

export function getMaxSessionDate(session: PendingLiveSession, existingSession?: LiveSession) {
	const isDemoMode = isLiveSessionDemoMode(session);
	const maxEnd = isDemoMode
		? new Date(Date.now() + MAX_DURATION_DEMO)
		: new Date(session.startTime.getTime() + MAX_DURATION);
	if (!existingSession || isDemoMode) {
		return { maxEnd };
	}

	const existingEndTime = new Date(existingSession.endTime);
	return { maxEnd: existingEndTime.getTime() > maxEnd.getTime() ? existingEndTime : maxEnd };
}

export function formatLiveSessionDate(date: Date) {
	return date.toLocaleDateString([], { dateStyle: "long" });
}

export function formatLiveSessionTime(time: Date) {
	return time.toLocaleTimeString([], { timeStyle: "short" });
}
